import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { FaChevronDown, FaCheck, FaSearch } from 'react-icons/fa';

/**
 * Selector con buscador. Solo deja elegir de la lista (no se escribe el valor a mano): sirve para tablas, columnas, funciones y
 * pestañas que vienen del sistema conectado. `options`: [{ value, label?, hint?, tags?: string[] }].
 * Si el valor guardado ya no está en la lista (se borró la columna), se muestra marcado para que se elija otro.
 * `multiple`: `value` es un arreglo y se marcan varias.
 */
export default function Combo({ value, onChange, options, placeholder = 'Elige…', ariaLabel, disabled = false, multiple = false, loading = false, emptyText = 'No hay opciones.', className = '' }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const box = useRef(null);
  const search = useRef(null);
  const listId = useId();
  const values = multiple ? (Array.isArray(value) ? value : []) : value ? [value] : [];
  const known = new Set(options.map((o) => o.value));
  const missing = values.filter((v) => !known.has(v));
  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    return t ? options.filter((o) => `${o.value} ${o.label || ''} ${o.hint || ''}`.toLowerCase().includes(t)) : options;
  }, [options, q]);

  useEffect(() => {
    if (!open) return undefined;
    setActive(0);
    requestAnimationFrame(() => search.current?.focus());
    const close = (e) => { if (!box.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const pick = (o) => {
    if (multiple) onChange(values.includes(o.value) ? values.filter((v) => v !== o.value) : [...values, o.value]);
    else { onChange(o.value); setOpen(false); setQ(''); }
  };
  const onKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, filtered.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (filtered[active]) pick(filtered[active]); }
    else if (e.key === 'Escape') { e.preventDefault(); setOpen(false); }
  };
  const label = (v) => options.find((o) => o.value === v)?.label || v;
  const shown = multiple
    ? (values.length ? values.map(label).join(', ') : placeholder)
    : (value ? label(value) : placeholder);

  return (
    <div ref={box} className={`relative ${className}`}>
      <button type="button" disabled={disabled} onClick={() => setOpen((o) => !o)} aria-haspopup="listbox" aria-expanded={open} aria-label={ariaLabel}
        className={`w-full h-10 flex items-center gap-2 rounded-xl border bg-raised/60 px-3 text-left text-[13px] disabled:opacity-50 disabled:cursor-not-allowed
          focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/60 ${missing.length ? 'border-caution' : 'border-line'}`}>
        <span className={`flex-1 truncate ${values.length ? 'text-fg font-mono text-[12.5px]' : 'text-fg-faint'}`}>{loading ? 'Cargando…' : shown}</span>
        <FaChevronDown aria-hidden className="h-2.5 w-2.5 text-fg-faint shrink-0" />
      </button>
      {missing.length > 0 && <p className="mt-1 text-[12px] text-caution">{missing.length === 1 ? `«${missing[0]}» ya no existe: elige otro.` : `Ya no existen: ${missing.join(', ')}.`}</p>}
      {open && (
        <div className="absolute z-40 mt-1 w-full min-w-[240px] rounded-xl bg-panel ring-1 ring-line p-1.5" style={{ boxShadow: 'var(--shadow)' }}>
          <div className="relative mb-1">
            <FaSearch aria-hidden className="absolute left-2.5 top-1/2 -translate-y-1/2 h-2.5 w-2.5 text-fg-faint" />
            <input ref={search} value={q} onChange={(e) => { setQ(e.target.value); setActive(0); }} onKeyDown={onKey} placeholder="Buscar"
              aria-label="Buscar en la lista" aria-controls={listId} aria-activedescendant={filtered[active] ? `${listId}-${active}` : undefined}
              className="w-full h-8 rounded-lg bg-raised pl-7 pr-2 text-[12.5px] text-fg placeholder:text-fg-faint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/60" />
          </div>
          <ul id={listId} role="listbox" aria-multiselectable={multiple || undefined} className="max-h-64 overflow-y-auto">
            {loading && <li className="px-2.5 py-2 text-[12.5px] text-fg-faint">Cargando…</li>}
            {!loading && !filtered.length && <li className="px-2.5 py-2 text-[12.5px] text-fg-faint">{q ? 'Nada coincide.' : emptyText}</li>}
            {filtered.map((o, i) => {
              const sel = values.includes(o.value);
              return (
                <li key={o.value} id={`${listId}-${i}`} role="option" aria-selected={sel} onMouseDown={(e) => e.preventDefault()} onClick={() => pick(o)} onMouseEnter={() => setActive(i)}
                  className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg cursor-pointer ${i === active ? 'bg-raised' : ''}`}>
                  <span className={`h-3.5 w-3.5 grid place-items-center shrink-0 ${sel ? 'text-live' : 'text-transparent'}`}><FaCheck aria-hidden className="h-2.5 w-2.5" /></span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-mono text-[12.5px] text-fg">{o.label || o.value}</span>
                    {o.hint && <span className="block truncate text-[11.5px] text-fg-faint">{o.hint}</span>}
                  </span>
                  {(o.tags || []).map((t) => <span key={t} className="shrink-0 rounded-md bg-raised px-1.5 py-0.5 text-[10.5px] font-semibold text-fg-dim">{t}</span>)}
                </li>
              );
            })}
          </ul>
          {multiple && values.length > 0 && (
            <div className="flex justify-end pt-1 border-t border-line mt-1">
              <button type="button" onClick={() => onChange([])} className="h-7 px-2.5 rounded-lg text-[12px] text-fg-dim hover:text-fg hover:bg-raised">Quitar todas</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
